import { useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Pencil } from "lucide-react";

const joinAddress = (a = {}) => [a.street, a.city, a.state, a.zip, a.country].filter(Boolean).join(", ");

const AddressFields = ({ title, addr, onField, streetTestId }) => (
  <>
    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground pt-2">{title}</p>
    <div className="space-y-1"><Label>Street</Label><Input data-testid={streetTestId} value={addr.street || ""} onChange={(e) => onField("street", e.target.value)} /></div>
    <div className="grid grid-cols-3 gap-3">
      <div className="space-y-1"><Label>City</Label><Input value={addr.city || ""} onChange={(e) => onField("city", e.target.value)} /></div>
      <div className="space-y-1"><Label>State</Label><Input value={addr.state || ""} onChange={(e) => onField("state", e.target.value)} /></div>
      <div className="space-y-1"><Label>ZIP</Label><Input value={addr.zip || ""} onChange={(e) => onField("zip", e.target.value)} /></div>
    </div>
    <div className="space-y-1"><Label>Country</Label><Input value={addr.country || ""} onChange={(e) => onField("country", e.target.value)} /></div>
  </>
);

export const ContactCard = ({ customer, customerId, onUpdated }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);

  const startEdit = () => {
    setForm({
      first_name: customer.first_name, last_name: customer.last_name,
      email: customer.email || "", phone: customer.phone || "",
      address: { ...(customer.address || {}) },
      shipping_address: { ...(customer.shipping_address || {}) },
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    try {
      const { data } = await api.patch(`/customers/${customerId}`, { ...form, email: form.email || null });
      onUpdated(data);
      setEditing(false);
      toast.success("Contact details saved.");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setAddr = (key) => (field, val) => setForm({ ...form, [key]: { ...form[key], [field]: val } });

  return (
    <Card className="shadow-sm" data-testid="contact-card">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="font-serif text-xl">Contact details</CardTitle>
        {!editing && (
          <Button variant="ghost" size="sm" onClick={startEdit} data-testid="edit-contact-btn">
            <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {editing ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>First name</Label><Input data-testid="edit-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
              <div className="space-y-1"><Label>Last name</Label><Input data-testid="edit-last-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
            </div>
            <div className="space-y-1"><Label>Email</Label><Input data-testid="edit-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="space-y-1"><Label>Phone</Label><Input data-testid="edit-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <AddressFields title="Billing address" addr={form.address} onField={setAddr("address")} />
            <AddressFields title="Shipping address" addr={form.shipping_address} onField={setAddr("shipping_address")} streetTestId="edit-ship-street" />
            <div className="flex gap-2 pt-1">
              <Button size="sm" onClick={saveEdit} data-testid="save-contact-btn">Save</Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)} data-testid="cancel-edit-btn">Cancel</Button>
            </div>
          </div>
        ) : (
          <>
            <p data-testid="contact-email"><span className="text-muted-foreground">Email:</span> {customer.email || "—"}</p>
            <p><span className="text-muted-foreground">Phone:</span> {customer.phone || "—"}</p>
            <p data-testid="billing-address"><span className="text-muted-foreground">Billing address:</span>{" "}
              {joinAddress(customer.address) || "—"}
            </p>
            <p data-testid="shipping-address"><span className="text-muted-foreground">Shipping address:</span>{" "}
              {joinAddress(customer.shipping_address) || "Not on file"}
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};
